import moment, { Moment } from 'moment';
import { DatePickerModule } from 'primeng/datepicker';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { PrimeNG } from 'primeng/config';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { environment } from '@environment';
import { TranslateLangPipe } from '@gs-2.0/shared';

import { EnvironmentNames } from '../../_core/constants/environments';
import { CustomControl } from '../../_core/models/CustomForms.model';
import { GlobalConfigService } from '../../_core/services/global-config.service';
import { RECURRENCE_PATTERN, RECURRENCE_RANGE_END } from './recurring-form.models';

@Component({
  selector: 'app-recurring-form',
  templateUrl: './recurring-form.component.html',
  styleUrls: ['./recurring-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DatePickerModule,
    RadioButtonModule,
    InputNumberModule,
    TranslateLangPipe,
  ],
})
export class RecurringFormComponent implements OnInit, OnDestroy {
  @Input() control: CustomControl;
  @Input() dateFormat: string;
  @Input() isStripe = false;
  @Input() hideNoEndDate = false;
  @Input() disableStartDate = false;
  @Input() checkGlobalConfig = false;

  private _destroy$ = new Subject<void>();

  minStart = moment().startOf('day').toISOString();
  minEnd: string;
  recurrences: string[];
  endOptions = RECURRENCE_RANGE_END;

  recurrencePattern!: UntypedFormControl;
  recurrenceRange!: UntypedFormGroup;

  // Signals for reactive date calculations
  private recurrencePatternValue = signal<RECURRENCE_PATTERN>(RECURRENCE_PATTERN.WEEKLY);

  minStartDate = computed(() => {
    if (!this.minStart) {
      this.minStart = moment().startOf('day').toISOString();
    }
    return moment(this.minStart).toDate();
  });

  maxStartDate = computed(() => {
    const pattern = this.recurrencePatternValue();
    const maxDate = this.addPattern(moment(this.minStart), pattern);
    return this.isStripe ? maxDate.subtract(1, 'day').toDate() : maxDate.toDate();
  });

  minEndDate = computed(() => {
    return moment(this.minEnd || this.minStart).toDate();
  });

  dateFormatSignal = computed(() => {
    // Priority: @Input > GlobalConfig > fallback
    const format =
      this.dateFormat || this.globalConfigService.getGlobalConfig()?.dateFormat || 'mm/dd/yy';

    // Convert to PrimeNG format convention:
    // PrimeNG uses 'yy' for 4-digit year (not 'yyyy')
    // Convert standard format tokens (Java/Moment.js style) to PrimeNG style
    return format
      .replace(/yyyy/gi, 'yy')  // yyyy/YYYY → yy (4-digit year in PrimeNG)
      .replace(/MMMM/g, '\x02') // MMMM placeholder (avoid clobbering by MM/M replacements)
      .replace(/MMM/g, '\x01')  // MMM placeholder
      .replace(/MM/g, 'mm')     // MM (Angular 2-digit month) → mm (PrimeNG 2-digit month)
      .replace(/\bM\b/g, 'm')   // M (Angular month no-pad) → m (PrimeNG month no-pad)
      .replace(/\x02/g, 'MM')   // restore MMMM → MM (PrimeNG full month name)
      .replace(/\x01/g, 'M');   // restore MMM → M (PrimeNG abbrev month name)
  });

  get isSinngeber(): boolean {
    return environment.fund.environmentName === EnvironmentNames.SINNGEBER;
  }

  get controlRecurrencePattern(): UntypedFormControl {
    return this.control?.get('recurrencePattern') as UntypedFormControl;
  }

  get controlRecurrenceRange(): UntypedFormGroup {
    return this.control?.get('recurrenceRange') as UntypedFormGroup;
  }

  get controlRecurrenceStartDate(): UntypedFormControl {
    return this.control?.get('recurrenceRange.startDate') as UntypedFormControl;
  }

  get controlRecurrenceEndDate(): UntypedFormControl {
    return this.control?.get('recurrenceRange.endDate') as UntypedFormControl;
  }

  get controlRecurrenceRangeOption(): UntypedFormControl {
    return this.control?.get('recurrenceRange.rangeOption') as UntypedFormControl;
  }

  get controlRecurrenceRangeOccurrences(): UntypedFormControl {
    return this.control?.get('recurrenceRange.rangeOccurrences') as UntypedFormControl;
  }

  get rangeEndErrors() {
    return this.control?.get('recurrenceRange')?.errors;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private globalConfigService: GlobalConfigService,
    private primeNGConfig: PrimeNG
  ) {}

  private momentToDate(momentDate: Moment | string | null): Date | null {
    if (!momentDate) return null;
    return moment(momentDate).toDate();
  }

  ngOnInit(): void {
    // Configure PrimeNG locale for 3-character day names
    this.primeNGConfig.setTranslation({
      dayNamesMin: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    });

    this.recurrencePattern = this.fb.control(RECURRENCE_PATTERN.WEEKLY, [Validators.required]);
    this.recurrenceRange = this.fb.group({
      startDate: [new Date(), Validators.required],
      rangeOption: [null, Validators.required],
      endDate: [null],
      rangeOccurrences: [2],
    });

    this.setRecurrencePatterns();
    this.setFormValues();
    this.setControlValueChanges();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  setRecurrencePatterns(): void {
    this.recurrences = this.checkGlobalConfig
      ? this.globalConfigService.getGlobalConfig().recurrencePattern
      : Object.keys(RECURRENCE_PATTERN);
  }

  setFormValues(): void {
    const patternValue = this.controlRecurrencePattern?.value || RECURRENCE_PATTERN.WEEKLY;
    this.recurrencePattern.patchValue(patternValue);
    this.recurrencePatternValue.set(patternValue); // Initialize signal
    this.recurrenceRange.patchValue({
      startDate: this.momentToDate(this.controlRecurrenceStartDate?.value),
      rangeOption: this.controlRecurrenceRangeOption?.value,
      endDate: this.momentToDate(this.controlRecurrenceEndDate?.value),
      rangeOccurrences: this.controlRecurrenceRangeOccurrences?.value,
    });
    this.setCalendarMinDate(this.recurrencePattern.value);
    if (this.control?.disabled) {
      this.recurrencePattern.disable({ emitEvent: false });
      this.recurrenceRange.disable({ emitEvent: false });
    }
  }

  setControlValueChanges(): void {
    this.recurrencePattern.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe((recurrencePattern) => {
        this.recurrencePatternValue.set(recurrencePattern); // Update signal
        this.control?.patchValue({ recurrencePattern });
        !this.disableStartDate && this.recurrenceRange.patchValue({ startDate: new Date() });
        this.setCalendarMinDate(recurrencePattern);
      });

    this.recurrenceRange
      .get('startDate')
      .valueChanges.pipe(takeUntil(this._destroy$))
      .subscribe((startDate: Date) => {
        const startMoment = this.setToCurrentTime(moment(startDate));
        const recurrencePattern = this.recurrencePattern.value;
        const endDateBy = moment(startMoment)
          .add(this.getMultiplier(recurrencePattern), this.getAddType(recurrencePattern))
          .valueOf();

        this.recurrenceRange
          .get('endDate')
          ?.patchValue(this.momentToDate(moment(endDateBy)), { emitEvent: false });
        this.controlRecurrenceStartDate?.patchValue(startMoment.valueOf());

        this.setCalendarMinDate(recurrencePattern);
      });

    this.recurrenceRange
      .get('rangeOccurrences')
      .valueChanges.pipe(takeUntil(this._destroy$))
      .subscribe((num) => {
        if (num && !Number.isInteger(num)) {
          this.recurrenceRange.get('rangeOccurrences').patchValue(Math.round(num));
        }
      });

    this.recurrenceRange
      .get('rangeOption')
      .valueChanges.pipe(takeUntil(this._destroy$))
      .subscribe((rangeOption) => {
        const endDateControl = this.recurrenceRange.get('endDate');
        if (rangeOption === RECURRENCE_RANGE_END.ENDAFTER || rangeOption === RECURRENCE_RANGE_END.NOEND) {
          endDateControl?.disable({ emitEvent: false });
        } else {
          endDateControl?.enable({ emitEvent: false });
        }
      });

    this.recurrenceRange.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe(({ rangeOption, endDate, rangeOccurrences }) => {
        const startDate = this.controlRecurrenceStartDate?.value;
        const recurrencePattern = this.recurrencePattern.value;
        const endDateAfterOccurrences = moment(startDate)
          .add(
            this.getMultiplier(recurrencePattern, rangeOccurrences),
            this.getAddType(recurrencePattern)
          )
          .valueOf();
        const getEndDate =
          rangeOption === RECURRENCE_RANGE_END.ENDAFTER
            ? endDateAfterOccurrences
            : rangeOption === RECURRENCE_RANGE_END.NOEND
            ? ''
            : this.setToCurrentTime(moment(endDate), moment(startDate)).valueOf();

        this.controlRecurrenceRange?.patchValue({
          rangeOption,
          endDate: getEndDate,
          rangeOccurrences,
        });

        if (rangeOption === RECURRENCE_RANGE_END.ENDAFTER) {
          this.recurrenceRange.get('endDate')?.patchValue(
            this.momentToDate(moment(endDateAfterOccurrences)),
            { emitEvent: false }
          );
        }
      });
  }

  setCalendarMinDate(recurrencePattern: RECURRENCE_PATTERN): void {
    const startDate = moment(this.controlRecurrenceStartDate?.value || new Date());
    const endDate = this.isStripe
      ? this.addPattern(startDate, recurrencePattern).add(1, 'day')
      : this.addPattern(startDate, recurrencePattern);
    this.minEnd = this.setToCurrentTime(endDate).startOf('day').toISOString();
    this.recurrenceRange.patchValue({ endDate: this.momentToDate(this.minEnd) });
  }

  addPattern(date: Moment, rangeOption: RECURRENCE_PATTERN): Moment {
    let numToAdd: number;
    const addType = this.getAddType(rangeOption);

    switch (rangeOption) {
      case RECURRENCE_PATTERN.WEEKLY:
      case RECURRENCE_PATTERN.MONTHLY:
      case RECURRENCE_PATTERN.QUARTERLY:
      case RECURRENCE_PATTERN.ANNUALLY:
        numToAdd = 1;
        break;
      case RECURRENCE_PATTERN.BI_WEEKLY:
        numToAdd = 2;
        break;
      case RECURRENCE_PATTERN.SEMI_ANNUALLY:
        numToAdd = 6;
        break;
    }

    return date.add(numToAdd, addType);
  }

  getAddType(pattern: RECURRENCE_PATTERN): 'weeks' | 'months' | 'quarters' | 'years' {
    switch (pattern) {
      case RECURRENCE_PATTERN.MONTHLY:
      case RECURRENCE_PATTERN.SEMI_ANNUALLY:
        return 'months';
      case RECURRENCE_PATTERN.QUARTERLY:
        return 'quarters';
      case RECURRENCE_PATTERN.ANNUALLY:
        return 'years';
      default:
        return 'weeks';
    }
  }

  getMultiplier(recurrencePattern: RECURRENCE_PATTERN, rangeOccurrences = 2): number {
    const occurrenceMultiplier = this.isStripe ? rangeOccurrences : rangeOccurrences - 1;

    switch (recurrencePattern) {
      case RECURRENCE_PATTERN.BI_WEEKLY:
        return 2 * occurrenceMultiplier;
      case RECURRENCE_PATTERN.SEMI_ANNUALLY:
        return 6 * occurrenceMultiplier;
      default:
        return 1 * occurrenceMultiplier;
    }
  }

  // Updates input date to match source to the millisecond
  setToCurrentTime(inputDate: any, sourceDate?: Moment): Moment {
    sourceDate = sourceDate ? moment(sourceDate) : moment(new Date());
    return moment(inputDate)
      .set('hour', sourceDate.get('hour'))
      .set('minute', sourceDate.get('minute'))
      .set('second', sourceDate.get('second'))
      .set('millisecond', sourceDate.get('millisecond'));
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key.length === 1 && /^[a-zA-Z!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\\/?]*$/.test(event.key))
      event.preventDefault();
  }
}
